import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import { logger, toError } from "@/utils/logger";
import { CreateFormData } from "@/components/create/types";
import {
  CreatePageState,
  CreatePageAction,
  useCreatePageStore,
} from "@/stores/createPageStore";
import { useTicketQuery } from "@/hooks/useTicketQuery";
import { FormPersistenceService } from "@/services/formPersistenceService";
import { TICKET_RESTORE_TIMEOUT_MESSAGE } from "@/services/ticketService";
import SavePoint from "@/utils/savePoint";
import { CREATE_AUTH_HANDOFF_PARAM } from "@/utils/createAuthHandoffPersistence";
import {
  cancelTrackedCreateAuthHandoff,
  clearTrackedCreateAuthHandoff,
} from "@/utils/createAuthHandoffLifecycle";
import {
  clearQuoteResumeAfterAuth,
  QUOTE_RESUME_ID_PARAM,
} from "@/utils/quoteResumePersistence";
import { CREDIT_RESUME_ID_PARAM } from "@/utils/creditAuthIntentPersistence";
import {
  CREDIT_CHECKOUT_RESTORE_ID_PARAM,
  getCreditCheckoutRestoreId,
} from "@/utils/creditCheckoutReturnUrl";
import {
  hasMeaningfulFormData,
  serializeFormState,
} from "../utils/formStateHelpers";
import {
  createAuthoritativeCreateRestoreTargetKey,
  createUrlParamsKey,
  UrlParamValues,
} from "../utils/urlParamHelpers";
import { markAgentSessionReset } from "../utils/agentSessionId";

interface UseInitializationProps {
  state: CreatePageState;
  dispatch: React.Dispatch<CreatePageAction>;
  session: any;
  authLoading: boolean;
  showToast: (message: string, type: "success" | "error" | "warning") => void;
  updateFormData: (data: Partial<CreateFormData>) => void;
  searchParamValues: UrlParamValues;
  modifyParam: string | null;
  galleryDesignParam: string | null;
  galleryActionParam?: string | null;
  ticketIdParam: string | null;
  restoreFormRequested?: boolean;
  restoreIntentId?: string | null;
  anonymousWorkAdoptionFailed?: boolean;
  agentSessionRestorationPending?: boolean;
}

function clearAuthoritativeCreateState(
  dispatch: React.Dispatch<CreatePageAction>
): void {
  useCreatePageStore.getState().abortActiveRequest();
  dispatch({ type: "RESET_ALL" });

  try {
    localStorage.removeItem("createPageVariations");
  } catch {
    // Storage is best-effort; the in-memory reset still protects the view.
  }
}

function isTerminalTicketRestoreError(error: Error): boolean {
  return /ticket not found|access denied/i.test(error.message);
}

/**
 * Independent deadline for the complete ticket-restoration attempt. When it
 * expires, the query is cancelled and that attempt is permanently retired so
 * a late response cannot replace work the customer starts afterward.
 */
export const CREATE_RESTORATION_DEADLINE_MS = 12_000;

export function useInitialization({
  state,
  dispatch,
  session,
  authLoading,
  showToast,
  updateFormData,
  searchParamValues,
  modifyParam,
  galleryDesignParam,
  galleryActionParam = null,
  ticketIdParam,
  restoreFormRequested = false,
  restoreIntentId: restoreIntentIdProp = null,
  anonymousWorkAdoptionFailed = false,
  agentSessionRestorationPending = false,
}: UseInitializationProps) {
  // Track when restoration happened to prevent auto-save from overwriting
  const restorationTimestampRef = useRef<number | null>(null);
  // Track if restoration has been completed to prevent duplicate restorations
  const hasRestoredRef = useRef<boolean>(false);
  // Track processed URL params to prevent infinite loops
  const processedUrlParamsRef = useRef<string | null>(null);
  // Track previous param values to detect when they change from value to null
  const prevGalleryDesignParamRef = useRef<string | null>(null);
  const prevModifyParamRef = useRef<string | null>(null);
  const prevTicketIdParamRef = useRef<string | null>(null);
  // Track previous param values to detect when they're removed (for modification mode)
  const prevModifyParamForModeRef = useRef<string | null>(null);
  const prevGalleryDesignParamForModeRef = useRef<string | null>(null);
  // Track the last meaningful signature to avoid duplicate runs in Strict Mode/dev
  const restorationSignatureRef = useRef<string | null>(null);
  // An explicit ticket, modification, or gallery target owns the page while it
  // is being restored. This ref lets us clear an earlier design once per target
  // before the browser can paint it under the new URL.
  const preparedRestoreTargetRef = useRef<string | null>(null);
  // Remember errors we placed in the shared create error surface so a later
  // successful retry can clear only that error, not an unrelated one.
  const displayedTicketRestoreErrorRef = useRef<string | null>(null);
  // A terminal failure may cause several store-driven rerenders. Clear the
  // requested target once per failure without repeatedly resetting the error
  // that explains why its data is unavailable.
  const clearedTerminalTicketFailureRef = useRef<string | null>(null);

  const router = useRouter();

  // Determine which ticket ID to use for React Query
  const ticketIdForQuery = modifyParam || ticketIdParam || null;
  const browserUrl =
    typeof window !== "undefined" ? new URL(window.location.href) : null;
  const isExplicitBackGeneration = Boolean(
    ticketIdParam && browserUrl?.searchParams.get("step") === "3"
  );
  const authoritativeTicketMode = modifyParam
    ? "modify"
    : ticketIdParam
      ? isExplicitBackGeneration
        ? "back"
        : "review"
      : null;
  const baseAuthoritativeRestoreTargetKey =
    createAuthoritativeCreateRestoreTargetKey({
      modifyParam,
      ticketIdParam,
      galleryDesignParam,
    });
  const authoritativeRestoreTargetKey = baseAuthoritativeRestoreTargetKey
    ? `${baseAuthoritativeRestoreTargetKey}:${authoritativeTicketMode ?? "default"}`
    : null;
  const checkoutRestoreId = browserUrl
    ? getCreditCheckoutRestoreId(browserUrl)
    : null;
  const isCreditPurchaseReturn =
    browserUrl?.searchParams.get("credits_purchased") === "true";
  const resolvedRestoreFormRequested =
    (restoreFormRequested ||
      browserUrl?.searchParams.get("restore_form") === "true") &&
    (!isCreditPurchaseReturn || !!checkoutRestoreId);
  const resolvedRestoreIntentId =
    checkoutRestoreId ??
    restoreIntentIdProp ??
    browserUrl?.searchParams.get(CREATE_AUTH_HANDOFF_PARAM) ??
    browserUrl?.searchParams.get(QUOTE_RESUME_ID_PARAM) ??
    browserUrl?.searchParams.get(CREDIT_RESUME_ID_PARAM) ??
    null;
  const restoreRequestKey =
    [
      resolvedRestoreFormRequested || resolvedRestoreIntentId
        ? `savepoint:${resolvedRestoreFormRequested ? "restore" : "intent"}:${resolvedRestoreIntentId ?? "canonical"}`
        : null,
      authoritativeRestoreTargetKey,
      anonymousWorkAdoptionFailed ? "anonymous-adoption-failed" : null,
    ]
      .filter(Boolean)
      .join("|") || null;
  const [restorationPending, setRestorationPending] = useState(
    Boolean(restoreRequestKey)
  );
  const [ticketRestoreAttempt, setTicketRestoreAttempt] = useState(0);
  const ticketRestoreAttemptKey =
    ticketIdForQuery && authoritativeRestoreTargetKey
      ? `${authoritativeRestoreTargetKey}|attempt:${ticketRestoreAttempt}`
      : null;
  const retiredTicketRestoreAttemptKeyRef = useRef<string | null>(null);
  const [retiredTicketRestoreAttemptKey, setRetiredTicketRestoreAttemptKey] =
    useState<string | null>(null);
  const currentTicketRestoreAttemptRetired = Boolean(
    ticketRestoreAttemptKey &&
    (retiredTicketRestoreAttemptKeyRef.current === ticketRestoreAttemptKey ||
      retiredTicketRestoreAttemptKey === ticketRestoreAttemptKey)
  );
  const pendingRestoreRequestKeyRef = useRef<string | null>(restoreRequestKey);
  const previousRestoreRequestKeyRef = useRef<string | null>(restoreRequestKey);
  const projectRestoreBoundaryRef = useRef<string | null>(null);
  const searchParamsKey = createUrlParamsKey(searchParamValues);
  const hasFrontImage =
    !!state.generation.images.front.url || !!state.generation.images.front.path;

  // Use React Query to fetch ticket data
  const {
    data: ticketData,
    isLoading: isLoadingTicket,
    isFetching: isFetchingTicket,
    error: ticketError,
    cancelTicketQuery,
  } = useTicketQuery(ticketIdForQuery, session?.user?.id ?? "signed-out", {
    restoreAttempt: ticketRestoreAttempt,
    enabled: !currentTicketRestoreAttemptRetired,
  });
  const cancelTicketQueryRef = useRef(cancelTicketQuery);
  cancelTicketQueryRef.current = cancelTicketQuery;
  const ticketRestoreAttemptKeyRef = useRef(ticketRestoreAttemptKey);
  ticketRestoreAttemptKeyRef.current = ticketRestoreAttemptKey;

  const retryTicketRestoration = useCallback(() => {
    if (!ticketIdForQuery) return;
    retiredTicketRestoreAttemptKeyRef.current = null;
    setRetiredTicketRestoreAttemptKey(null);
    restorationSignatureRef.current = null;
    displayedTicketRestoreErrorRef.current = null;
    clearedTerminalTicketFailureRef.current = null;
    if (useCreatePageStore.getState().generation.errors.message) {
      dispatch({ type: "CLEAR_ERROR" });
    }
    setRestorationPending(true);
    // A new key is the refetch boundary. It cannot reuse data or terminal state
    // from the attempt that was retired by the deadline.
    setTicketRestoreAttempt(attempt => attempt + 1);
  }, [dispatch, ticketIdForQuery]);

  // Claim a newly requested restore before its async ticket/session data can
  // arrive. The previous dress may still have an unabortable render in flight;
  // invalidating it only when the replacement snapshot applies is too late.
  useLayoutEffect(() => {
    if (
      !restoreRequestKey ||
      projectRestoreBoundaryRef.current === restoreRequestKey
    ) {
      return;
    }

    projectRestoreBoundaryRef.current = restoreRequestKey;
    useCreatePageStore.getState().advanceProjectActionEpoch();
  }, [restoreRequestKey]);

  // A client-side navigation can introduce a restoration target without
  // remounting the create page. Claim the surface in a layout effect so the
  // fresh-entry UI (or a prior dress) cannot paint before restoration starts.
  useLayoutEffect(() => {
    if (pendingRestoreRequestKeyRef.current === restoreRequestKey) return;

    const displayedTicketRestoreError = displayedTicketRestoreErrorRef.current;
    if (
      displayedTicketRestoreError &&
      useCreatePageStore.getState().generation.errors.message ===
        displayedTicketRestoreError
    ) {
      dispatch({ type: "CLEAR_ERROR" });
    }
    displayedTicketRestoreErrorRef.current = null;
    retiredTicketRestoreAttemptKeyRef.current = null;
    setRetiredTicketRestoreAttemptKey(null);
    pendingRestoreRequestKeyRef.current = restoreRequestKey;
    setRestorationPending(Boolean(restoreRequestKey));
  }, [dispatch, restoreRequestKey]);

  useEffect(() => {
    if (
      !ticketIdForQuery ||
      !ticketRestoreAttemptKey ||
      !restorationPending ||
      currentTicketRestoreAttemptRetired
    ) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (
        ticketRestoreAttemptKeyRef.current !== ticketRestoreAttemptKey ||
        retiredTicketRestoreAttemptKeyRef.current === ticketRestoreAttemptKey
      ) {
        return;
      }

      // Retire synchronously before cancellation can publish a query-state
      // update. Every restoration branch checks this generation boundary.
      retiredTicketRestoreAttemptKeyRef.current = ticketRestoreAttemptKey;
      displayedTicketRestoreErrorRef.current = TICKET_RESTORE_TIMEOUT_MESSAGE;
      setRetiredTicketRestoreAttemptKey(ticketRestoreAttemptKey);
      setRestorationPending(false);
      if (
        useCreatePageStore.getState().generation.errors.message !==
        TICKET_RESTORE_TIMEOUT_MESSAGE
      ) {
        dispatch({
          type: "SET_ERROR",
          payload: TICKET_RESTORE_TIMEOUT_MESSAGE,
        });
      }
      void cancelTicketQueryRef.current?.();
    }, CREATE_RESTORATION_DEADLINE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [
    currentTicketRestoreAttemptRetired,
    dispatch,
    restorationPending,
    ticketIdForQuery,
    ticketRestoreAttemptKey,
  ]);

  // A client-side A -> B navigation reuses this hook and its Zustand store.
  // Clear A synchronously when a ticket, modification, or gallery design
  // becomes authoritative; waiting for restoration effects would leave A
  // visible during loading and forever on a terminal 403/404. The mode belongs
  // in the key so changing the same id from back-generation to modification (or
  // vice versa) also prepares a clean view before paint.
  useLayoutEffect(() => {
    if (!authoritativeRestoreTargetKey) {
      preparedRestoreTargetRef.current = null;
      clearedTerminalTicketFailureRef.current = null;
      return;
    }

    const previousTargetKey = preparedRestoreTargetRef.current;
    if (previousTargetKey === authoritativeRestoreTargetKey) {
      return;
    }
    preparedRestoreTargetRef.current = authoritativeRestoreTargetKey;
    restorationTimestampRef.current = null;
    clearedTerminalTicketFailureRef.current = null;

    const isClientTargetTransition = previousTargetKey !== null;
    const hasStateOwnedByExplicitTarget =
      state.ticket.id !== null || hasMeaningfulFormData(state);
    if (!isClientTargetTransition && !hasStateOwnedByExplicitTarget) {
      return;
    }

    clearAuthoritativeCreateState(dispatch);
  }, [authoritativeRestoreTargetKey, state, dispatch]);

  // Track if we've attempted duplication to prevent infinite loops
  const duplicationAttemptedRef = useRef<string | null>(null);

  // Handle gallery design duplication after login (runs before restoration)
  useEffect(() => {
    // Only run if we have a session (user is logged in), auth is not loading,
    // and we have a gallery_design param with an id
    if (
      galleryActionParam === "customize" ||
      !session ||
      authLoading ||
      !galleryDesignParam
    ) {
      return;
    }

    // Check if we've already tried to duplicate this specific gallery_design
    if (duplicationAttemptedRef.current === galleryDesignParam) {
      return;
    }

    try {
      const parsed = (() => {
        try {
          return JSON.parse(galleryDesignParam);
        } catch (error) {
          try {
            return JSON.parse(decodeURIComponent(galleryDesignParam));
          } catch {
            throw error;
          }
        }
      })();
      const ticketId = parsed.id;

      // If gallery_design has a ticket ID, try to duplicate it
      if (ticketId && typeof ticketId === "string") {
        // Mark that we're attempting duplication for this specific param
        duplicationAttemptedRef.current = galleryDesignParam;

        logger.debug("Attempting to duplicate ticket after login", {
          component: "useInitialization",
          ticketId,
          hasLocationData: !!(
            parsed.city ||
            parsed.state ||
            parsed.zip_code ||
            parsed.country
          ),
        });

        // Try to duplicate the ticket
        fetch("/api/gallery/duplicate", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            originalTicketId: ticketId,
            ...(parsed.city && { city: parsed.city }),
            ...(parsed.state && { state: parsed.state }),
            ...(parsed.zip_code && { zip_code: parsed.zip_code }),
            ...(parsed.country && { country: parsed.country }),
          }),
        })
          .then(response => {
            if (response.ok) {
              return response.json();
            }
            // If duplication fails, clear the flag and let the normal flow continue
            logger.debug("Duplication failed, falling back to normal flow", {
              component: "useInitialization",
              ticketId,
              status: response.status,
            });
            duplicationAttemptedRef.current = null;
            return null;
          })
          .then(data => {
            if (data?.ticket?.id) {
              // Successfully duplicated - redirect to ticket page
              logger.debug("Successfully duplicated ticket, redirecting", {
                component: "useInitialization",
                newTicketId: data.ticket.id,
              });
              router.push(`/ticket/${data.ticket.id}`);
            }
            // If no data, the normal gallery_design flow will continue
          })
          .catch(err => {
            logger.error(
              "Error duplicating ticket after login",
              { component: "useInitialization", ticketId },
              toError(err)
            );
            // Clear the flag on error so user can try again if needed
            duplicationAttemptedRef.current = null;
          });
      }
    } catch (err) {
      // If parsing fails, just continue with normal flow
      logger.debug("Could not parse gallery_design for duplication", {
        component: "useInitialization",
        error: err instanceof Error ? err.message : "Unknown error",
      });
    }
  }, [session, authLoading, galleryActionParam, galleryDesignParam, router]);

  // Both the session-adoption and form-restoration effects need to observe the
  // server marker first. Once auth has settled, remove the one-shot redirect
  // flags even when there is no usable save point; otherwise every later chat
  // session or reload in this tab is treated as the same failed adoption.
  useEffect(() => {
    if (authLoading || restorationPending || typeof window === "undefined")
      return;

    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has("_anon_work_adopted")) return;

    const cleanupTimer = window.setTimeout(() => {
      const latestUrl = new URL(window.location.href);
      latestUrl.searchParams.delete("_anon_work_adopted");
      latestUrl.searchParams.delete("restore_form");
      latestUrl.searchParams.delete(CREATE_AUTH_HANDOFF_PARAM);
      window.history.replaceState(
        {},
        "",
        latestUrl.pathname + latestUrl.search + latestUrl.hash
      );
    }, 100);

    return () => window.clearTimeout(cleanupTimer);
  }, [authLoading, restorationPending, session?.user?.id]);

  // Unified restoration effect - handles all restoration priorities
  useEffect(() => {
    // Every explicit restoration target is authoritative. Reset when a target
    // is added, removed, or replaced so a same-component navigation from one
    // ticket to another cannot keep the previous dress in the store.
    const restorationParamsChanged =
      prevGalleryDesignParamRef.current !== galleryDesignParam ||
      prevModifyParamRef.current !== modifyParam ||
      prevTicketIdParamRef.current !== ticketIdParam;
    const restoreRequestChanged =
      previousRestoreRequestKeyRef.current !== restoreRequestKey;

    if (restorationParamsChanged || restoreRequestChanged) {
      hasRestoredRef.current = false;
    }

    // Also reset if all params are null and no images are loaded
    const allParamsNull = !galleryDesignParam && !modifyParam && !ticketIdParam;
    const noImagesLoaded =
      !state.generation.images.front.url && !state.generation.images.front.path;
    const url = new URL(window.location.href);
    const shouldRestore = resolvedRestoreFormRequested;
    const restoreIntentId = resolvedRestoreIntentId;

    if (allParamsNull && noImagesLoaded) {
      hasRestoredRef.current = false;
    }

    // Update previous param refs
    prevGalleryDesignParamRef.current = galleryDesignParam;
    prevModifyParamRef.current = modifyParam;
    prevTicketIdParamRef.current = ticketIdParam;
    previousRestoreRequestKeyRef.current = restoreRequestKey;

    // Early exit: If restoration has already been completed, skip
    if (hasRestoredRef.current) {
      setRestorationPending(false);
      return;
    }

    // A scoped browser snapshot contains the exact pre-auth draft, while the
    // adopted server session contains its ownership and concurrency metadata.
    // Apply them in that order of authority: hydrate the server session first,
    // then overlay the exact browser snapshot. Otherwise whichever async effect
    // happens to finish last can erase uploaded reference URLs or role data.
    if (
      agentSessionRestorationPending &&
      (resolvedRestoreFormRequested || resolvedRestoreIntentId)
    ) {
      return;
    }

    // Avoid overwriting an ordinary in-progress create session. Explicit URL
    // targets are different: `/create?ticket_id=...` and the modification /
    // gallery entry points must replace any stale Zustand image left behind by
    // an earlier client-side route.
    const hasExplicitRestorationTarget = Boolean(
      ticketIdForQuery || galleryDesignParam
    );
    if (hasFrontImage && !hasExplicitRestorationTarget && !shouldRestore) {
      hasRestoredRef.current = true;
      setRestorationPending(false);
      return;
    }

    // A deadline-retired attempt is immutable. Query cancellation normally
    // prevents a late result, but this guard is the correctness boundary when
    // a transport ignores AbortSignal or a stale observer publishes afterward.
    // In particular, do not clear the timeout error or apply ticket data here.
    if (ticketIdForQuery && currentTicketRestoreAttemptRetired) {
      setRestorationPending(false);
      return;
    }

    if (!ticketError) {
      clearedTerminalTicketFailureRef.current = null;
    }

    // Early exit: Wait for ticket data to load if we're fetching a ticket
    if (ticketIdForQuery && (isLoadingTicket || isFetchingTicket)) {
      return;
    }

    // Early exit: If ticket query failed, keep the previous create state out
    // of view and surface the precise ticket-service error through the
    // existing create error toast (for example, "Ticket not found" or
    // "Access denied"). A later successful refetch can still restore because
    // `hasRestoredRef` remains false.
    if (ticketIdForQuery && ticketError) {
      logger.debug("Ticket query failed, skipping prefill", {
        component: "useInitialization",
        ticketId: ticketIdForQuery,
        error: ticketError.message,
      });

      const terminalFailureSignature = `${authoritativeRestoreTargetKey || ticketIdForQuery}|${ticketError.message}`;
      if (
        isTerminalTicketRestoreError(ticketError) &&
        clearedTerminalTicketFailureRef.current !== terminalFailureSignature
      ) {
        clearedTerminalTicketFailureRef.current = terminalFailureSignature;
        clearAuthoritativeCreateState(dispatch);
      }

      displayedTicketRestoreErrorRef.current = ticketError.message;
      if (
        useCreatePageStore.getState().generation.errors.message !==
        ticketError.message
      ) {
        dispatch({ type: "SET_ERROR", payload: ticketError.message });
      }
      setRestorationPending(false);
      return;
    }

    const displayedTicketRestoreError = displayedTicketRestoreErrorRef.current;
    if (displayedTicketRestoreError) {
      displayedTicketRestoreErrorRef.current = null;
      if (state.generation.errors.message === displayedTicketRestoreError) {
        dispatch({ type: "CLEAR_ERROR" });
      }
    }

    // Avoid duplicate effect work for identical inputs (e.g., Strict Mode double-run)
    const restorationSignature = [
      modifyParam || "",
      galleryDesignParam || "",
      ticketIdParam || "",
      ticketIdForQuery || "",
      ticketData?.id || "",
      isLoadingTicket ? "loading" : "loaded",
      isFetchingTicket ? "fetching" : "settled",
      ticketError?.message || "",
      `restore-attempt:${ticketRestoreAttempt}`,
      session?.user?.id || "",
      authLoading ? "auth-loading" : "auth-ready",
      hasFrontImage ? "has-image" : "no-image",
      searchParamsKey,
      resolvedRestoreFormRequested ? "restore-form" : "no-restore-form",
      resolvedRestoreIntentId || "",
    ].join("|");

    if (restorationSignatureRef.current === restorationSignature) {
      return;
    }
    restorationSignatureRef.current = restorationSignature;

    // Check URL for restore_form parameter
    const creditsPurchased =
      url.searchParams.get("credits_purchased") === "true";
    const didAnonymousWorkAdoptionFail =
      anonymousWorkAdoptionFailed ||
      url.searchParams.get("_anon_work_adopted") === "0";

    logger.debug("useInitialization: Checking for restoration", {
      component: "useInitialization",
      shouldRestore,
      restoreIntentId,
      creditsPurchased,
      hasRestored: hasRestoredRef.current,
      hasImages: !!(
        state.generation.images.front.url || state.generation.images.front.path
      ),
      modifyParam: !!modifyParam,
      galleryDesignParam: !!galleryDesignParam,
      ticketIdParam: !!ticketIdParam,
      session: !!session,
      authLoading,
    });

    // Use FormPersistenceService to determine what to restore
    const restoration = FormPersistenceService.restoreState({
      ticketData: ticketData || undefined,
      ticketId: ticketIdForQuery || undefined,
      modifyParam: modifyParam || undefined,
      galleryDesignParam: galleryDesignParam || undefined,
      isForBackGeneration: authoritativeTicketMode === "back",
      session,
      authLoading,
      shouldRestore,
      restoreIntentId,
      searchParamValues,
      currentState: state,
    });

    logger.debug("useInitialization: Restoration result", {
      component: "useInitialization",
      restorationType: restoration.type,
      shouldRestore: restoration.shouldRestore,
      hasData: !!restoration.data,
    });

    if (!restoration.shouldRestore) {
      logger.debug("useInitialization: No restoration needed", {
        component: "useInitialization",
        restorationType: restoration.type,
      });

      // A failed anonymous-work adoption without a restorable save point must
      // also cancel the one-shot quote capability. Leaving it in the URL could
      // submit an unrelated design the user creates later under this account.
      if (didAnonymousWorkAdoptionFail) {
        cancelTrackedCreateAuthHandoff(
          url.searchParams.get(CREATE_AUTH_HANDOFF_PARAM)
        );
        clearQuoteResumeAfterAuth(url.searchParams.get(QUOTE_RESUME_ID_PARAM));
        url.searchParams.delete("quote_intent");
        url.searchParams.delete(QUOTE_RESUME_ID_PARAM);
        window.history.replaceState(
          {},
          "",
          url.pathname + url.search + url.hash
        );
      }

      // A forged, stale, or cross-account checkout capability is consumed
      // from the URL without touching its scoped SavePoint. This prevents a
      // later reload from retrying it while preserving the rightful tab's
      // ability to finish its own checkout return.
      if (checkoutRestoreId && !authLoading) {
        url.searchParams.delete(CREDIT_CHECKOUT_RESTORE_ID_PARAM);
        url.searchParams.delete("restore_form");
        window.history.replaceState(
          {},
          "",
          url.pathname + url.search + url.hash
        );
      }

      // Auth-dependent save points deliberately return `none` while the
      // session is hydrating. Keep ownership of the page until auth settles;
      // otherwise the anonymous entry point flashes between login and restore.
      if (!(shouldRestore && authLoading)) {
        setRestorationPending(false);
      }
      return;
    }

    // Mark as restored to prevent duplicate restorations
    hasRestoredRef.current = true;

    // Handle different restoration types
    if (
      restoration.type === "ticket_prefill" ||
      restoration.type === "gallery_prefill"
    ) {
      restorationTimestampRef.current = Date.now();
      // Ticket or gallery prefill - do NOT restore chat history
      dispatch({
        type: "RESTORE_IMAGE_STATE",
        payload: {
          ...restoration.data,
          // Restore only this target's saved contract, never conversational
          // memory left over from the previously open dress.
          designBrief: restoration.data?.designBrief ?? null,
          // An explicit ticket/gallery target replaces the design that was
          // previously open in this long-lived client store. The prefill data
          // does not carry generation variations, so clear them explicitly
          // instead of leaving thumbnails from the prior dress attached to
          // the newly requested ticket.
          frontVariations: null,
          backVariations: null,
          selectedFrontVariationIndex: null,
          selectedBackVariationIndex: null,
          restoreChatHistory: false, // Explicitly don't restore chat history
          restorePresets: false, // Explicitly don't restore presets
        },
      });

      const restoredStore = useCreatePageStore.getState();
      const hasRestoredImage = Boolean(
        restoration.data?.imageUrl || restoration.data?.imagePath
      );

      // Ticket/gallery prefill deliberately does not replay an unrelated chat
      // transcript. It still needs to enter the chat workspace, though, or the
      // chat-only create UI falls back to the occasion picker while the restored
      // dress is present in the store but invisible.
      if (hasRestoredImage) {
        restoredStore.beginCustomEntryChat("");
      }

      // For back generation, approve front design
      if (restoration.isForBackGeneration) {
        dispatch({ type: "APPROVE_FRONT_DESIGN" });
        restoredStore.setPhase("back_design");
      } else if (hasRestoredImage) {
        restoredStore.setPhase("front_review");
      }
      if (restoreIntentId) {
        SavePoint.clear(restoreIntentId);
      }
      clearTrackedCreateAuthHandoff(
        url.searchParams.get(CREATE_AUTH_HANDOFF_PARAM)
      );

      if (restoration.type === "gallery_prefill") {
        // Gallery Customize is a one-shot seed, not a permanent restore mode.
        // Once its snapshot owns the canvas, consume the bulky source params so
        // the first persisted conversation can become the canonical reload
        // target instead of being replaced by the public gallery baseline.
        // Persist the applied snapshot synchronously before consuming those
        // params. The normal debounced auto-save intentionally pauses after a
        // restore, so without this checkpoint a crash/reload in that window
        // could lose the customization and fall back to an older dress.
        const gallerySnapshot = useCreatePageStore.getState();
        if (hasMeaningfulFormData(gallerySnapshot)) {
          FormPersistenceService.saveState({
            data: serializeFormState(gallerySnapshot),
            source: "auto_save",
          });
        }
        // Treat that seed as a deliberate new conversation as well. Removing the
        // authoritative URL params otherwise lets the ordinary signed-in startup
        // fallback restore the customer's latest session over this fresh canvas.
        // The marker is spent automatically when the first new turn mints its id.
        markAgentSessionReset();
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("gallery_design");
        currentUrl.searchParams.delete("gallery_action");
        currentUrl.searchParams.delete("gallery_source");
        window.history.replaceState(
          window.history.state,
          "",
          currentUrl.pathname + currentUrl.search + currentUrl.hash
        );
      }
    } else if (restoration.type === "savepoint") {
      // SavePoint restoration - restore chat history if present
      restorationTimestampRef.current = Date.now();

      dispatch({
        type: "RESTORE_IMAGE_STATE",
        payload: {
          ...restoration.data,
          // Every new save point now carries its structured draft. Legacy
          // payloads explicitly clear any unrelated brief already in the
          // long-lived store rather than borrowing it for the restored dress.
          designBrief: restoration.data?.designBrief ?? null,
          // Existing-account sign-in normally adopts this ticket atomically.
          // If that server step explicitly failed, keep the locally restored
          // dress but create a fresh ticket owned by the proven account.
          ...(didAnonymousWorkAdoptionFail
            ? {
                ticketId: null,
                agentSessionId: null,
                agentSessionVersion: 0,
                agentMessageVersion: 0,
              }
            : {}),
          preserveAgentSessionIdentity: !didAnonymousWorkAdoptionFail,
          restoreChatHistory: true, // Restore chat history from SavePoint
          chatHistory: restoration.data?.formData?.chatHistory,
          restorePresets: true, // Restore quick action buttons from SavePoint
          presets:
            restoration.data?.formData?.quick_action_buttons ||
            restoration.data?.formData?.presets,
        },
      });

      // A save point with no transcript still needs to make its restored work
      // visible. Resume a prompt-only design in the composer; if a render is
      // present, enter the chat shell with an empty composer so the customer
      // sees the image without accidentally resubmitting its old prompt. Never
      // auto-send: the customer keeps control of the next turn.
      const restoredStore = useCreatePageStore.getState();
      const restoredPrompt = restoredStore.form.data.prompt?.trim();
      const hasRestoredConversation =
        restoredStore.agent.conversation.messages.length > 0;
      const hasRestoredRender = Boolean(
        restoredStore.generation.images.front.url ||
        restoredStore.generation.images.front.path ||
        restoredStore.generation.images.back.url ||
        restoredStore.generation.images.back.path
      );
      if (!hasRestoredConversation && (restoredPrompt || hasRestoredRender)) {
        restoredStore.beginCustomEntryChat(
          hasRestoredRender || restoredStore.agent.pendingSubmittedMessage
            ? ""
            : restoredPrompt || ""
        );
      }

      // Explicitly set step based on redirect source to prevent any state conflicts
      const savePoint = SavePoint.getLatestSavePoint(restoreIntentId);
      if (savePoint?.source === "auth_redirect") {
        // Always restore to step 1 for auth redirects - we want the form, not the generation step
        dispatch({ type: "SET_STEP", payload: 1 });
      } else if (savePoint?.source === "credit_purchase_redirect") {
        // For credit purchase redirects, use the target step from restoration
        // This ensures users return to the correct step (step 3 if they have front images, step 1 if no images)
        if (restoration.targetStep !== undefined) {
          dispatch({ type: "SET_STEP", payload: restoration.targetStep });
        }
      }

      // If returning from credit purchase, log it for debugging
      // Note: Credit invalidation is handled by useCreditManagement (detects credits_purchased param)
      // Note: Analytics tracking is handled by the /credits/success page before redirect
      if (savePoint?.source === "credit_purchase_redirect") {
        logger.debug(
          "useInitialization: Restoring from credit purchase redirect",
          {
            component: "useInitialization",
            hasSession: !!session,
            authLoading,
            savedAt: savePoint.savedAt,
            age: Date.now() - savePoint.savedAt,
          }
        );
        // Analytics tracking is already handled by /credits/success page
        // No need to call trackCreditPurchase() here - it would make a duplicate
        // API call to /api/stripe-session-details
      }

      // Clean up URL parameter
      setTimeout(() => {
        // Use a fresh URL to avoid re-introducing other params that may have been
        // cleaned up by other effects (e.g., credits_purchased in useCreditManagement)
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("restore_form");
        currentUrl.searchParams.delete(CREATE_AUTH_HANDOFF_PARAM);
        currentUrl.searchParams.delete(CREDIT_CHECKOUT_RESTORE_ID_PARAM);
        currentUrl.searchParams.delete("_anon_work_adopted");
        window.history.replaceState({}, "", currentUrl.toString());
      }, 100);

      if (restoration.toastMessage) {
        showToast(restoration.toastMessage, "success");
      }
      if (restoreIntentId) SavePoint.clear(restoreIntentId);
      else SavePoint.clear(undefined, "canonical");
      clearTrackedCreateAuthHandoff(
        url.searchParams.get(CREATE_AUTH_HANDOFF_PARAM)
      );
    } else if (restoration.type === "url_params") {
      // URL params restoration
      updateFormData(restoration.data);

      processedUrlParamsRef.current = createUrlParamsKey(searchParamValues);
    }
    setRestorationPending(false);
    // The broad objects used above are intentionally represented by the
    // primitive fields and stable keys below; depending on the whole Zustand
    // snapshot would rerun restoration after every dispatch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    modifyParam,
    galleryDesignParam,
    ticketIdParam,
    ticketData?.id,
    isLoadingTicket,
    isFetchingTicket,
    ticketError,
    session,
    authLoading,
    hasFrontImage,
    searchParamsKey,
    dispatch,
    showToast,
    updateFormData,
    ticketIdForQuery,
    ticketRestoreAttempt,
    currentTicketRestoreAttemptRetired,
    authoritativeTicketMode,
    authoritativeRestoreTargetKey,
    restoreFormRequested,
    restoreIntentIdProp,
    resolvedRestoreFormRequested,
    resolvedRestoreIntentId,
    checkoutRestoreId,
    restoreRequestKey,
    anonymousWorkAdoptionFailed,
    agentSessionRestorationPending,
  ]);

  // URL Parameters processing (separate effect for ongoing updates)
  useEffect(() => {
    if (state.ui.modification.isModificationMode) return;

    // Don't process URL params if a restoration just happened (within 5 seconds)
    if (
      restorationTimestampRef.current &&
      Date.now() - restorationTimestampRef.current < 5000
    ) {
      return;
    }

    // Don't process URL params if a higher priority restoration already happened
    // (ticket prefill or savepoint restoration)
    if (hasRestoredRef.current) {
      return;
    }

    // Don't process URL params if we have ticket data or modify/gallery params
    // These indicate higher priority restorations should take precedence
    if (modifyParam || galleryDesignParam || ticketIdParam) {
      return;
    }

    const {
      prompt: promptParam,
      length: lengthParam,
      sleeve: sleeveParam,
      neckline: necklineParam,
      silhouette: silhouetteParam,
      fabric: fabricParam,
      preset: presetParam,
      color: colorParam,
    } = searchParamValues;

    const hasUrlParams =
      promptParam ||
      lengthParam ||
      sleeveParam ||
      necklineParam ||
      silhouetteParam ||
      fabricParam ||
      presetParam ||
      colorParam;

    if (!hasUrlParams) {
      processedUrlParamsRef.current = null;
      return;
    }

    // Create a stable string representation of URL params to track changes
    const urlParamsKey = createUrlParamsKey(searchParamValues);

    // Skip if we've already processed these exact URL params
    if (processedUrlParamsRef.current === urlParamsKey) {
      return;
    }

    // Use FormPersistenceService to check URL params
    // Pass empty ticketData to ensure URL params are checked
    const restoration = FormPersistenceService.restoreState({
      ticketData: undefined,
      ticketId: undefined,
      modifyParam: undefined,
      galleryDesignParam: undefined,
      session,
      authLoading,
      shouldRestore: false,
      searchParamValues,
      currentState: state,
    });

    if (restoration.type === "url_params" && restoration.shouldRestore) {
      updateFormData(restoration.data);
      processedUrlParamsRef.current = urlParamsKey;
    } else {
      processedUrlParamsRef.current = urlParamsKey;
    }
    // URL/form inputs are represented by the primitive fields below so this
    // effect does not loop on object identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParamValues.prompt,
    searchParamValues.length,
    searchParamValues.sleeve,
    searchParamValues.neckline,
    searchParamValues.silhouette,
    searchParamValues.fabric,
    searchParamValues.preset,
    searchParamValues.color,
    state.ui.modification.isModificationMode,
    modifyParam,
    galleryDesignParam,
    ticketIdParam,
    session,
    authLoading,
    updateFormData,
    restorationTimestampRef,
  ]);

  // Modification Mode Detection
  useEffect(() => {
    // If we're in the middle of a restore-from-checkout, don't override step
    const url = new URL(window.location.href);
    const isRestoring = url.searchParams.get("restore_form") === "true";
    if (isRestoring) return;

    const isModificationMode = !!modifyParam || !!galleryDesignParam;
    const wasModificationMode =
      !!prevModifyParamForModeRef.current ||
      !!prevGalleryDesignParamForModeRef.current;

    // Only set modification mode if we actually have valid params
    // This prevents setting step to 4 in test scenarios where params are mocked but not actually set
    if (isModificationMode && !state.ui.modification.isModificationMode) {
      // Verify that we have actual modification params, not just mocked null values
      const hasValidModifyParam = modifyParam && modifyParam.trim().length > 0;
      const hasValidGalleryParam =
        galleryDesignParam && galleryDesignParam.trim().length > 0;

      if (hasValidModifyParam || hasValidGalleryParam) {
        dispatch({ type: "SET_MODIFICATION_MODE", payload: true });
        dispatch({ type: "SET_STEP", payload: 4 });
      }
    }

    // Clear modification mode when params are removed
    if (!isModificationMode && wasModificationMode) {
      dispatch({ type: "SET_MODIFICATION_MODE", payload: false });
      // A modify -> ticket transition has already restored the requested dress
      // for back generation at step 3. Do not overwrite that authoritative
      // navigation state with the ordinary modification-exit default.
      if (!ticketIdParam) {
        dispatch({ type: "SET_STEP", payload: 1 });
      }
    }

    // Update previous param refs
    prevModifyParamForModeRef.current = modifyParam;
    prevGalleryDesignParamForModeRef.current = galleryDesignParam;
  }, [
    modifyParam,
    galleryDesignParam,
    ticketIdParam,
    dispatch,
    state.ui.modification.isModificationMode,
  ]);

  // Reset restoration flags and clear only this browsing context's singleton
  // draft when the account changes. URL-scoped checkout/auth handoffs may
  // belong to another tab and must survive until that exact return consumes
  // them.
  const previousUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentUserId = session?.user?.id || null;
    const previousUserId = previousUserIdRef.current;

    // If user ID changed (different user logged in), clear save point
    if (
      previousUserId !== null &&
      currentUserId !== null &&
      previousUserId !== currentUserId
    ) {
      logger.debug(
        "New session detected (different user), clearing save point",
        {
          component: "useInitialization",
          previousUserId,
          currentUserId,
        }
      );
      FormPersistenceService.clearCanonicalState();
    }

    // If user logged out, clear save point
    if (previousUserId !== null && currentUserId === null) {
      logger.debug("User logged out, clearing save point", {
        component: "useInitialization",
      });
      FormPersistenceService.clearCanonicalState();
    }

    previousUserIdRef.current = currentUserId;

    // Reset restoration ref when session becomes null
    if (!session) {
      hasRestoredRef.current = false;
    }
  }, [session]);

  // Expose restorationTimestampRef for use by other hooks (e.g., usePersistence)
  return {
    restorationTimestampRef,
    restorationPending,
    retryTicketRestoration:
      ticketIdForQuery && (ticketError || currentTicketRestoreAttemptRetired)
        ? retryTicketRestoration
        : undefined,
  };
}
